import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';
import { isTerminalVerification, readProgramResult } from './managed-case-action-policy';
import { managedCaseStatusLabel, verificationOutcomeLabel } from './tiny-promo-labels';

interface ManagedCaseSummary {
  id: string;
  requestId: string;
  hubId: string;
  promoCode: string;
  promoterEmail: string;
  program: string;
  status: string;
  verificationOutcome?: string;
  whatsAppConnectionState: string;
  senderMismatch: boolean;
}

@Component({
  selector: 'app-tiny-promo-cases',
  templateUrl: './tiny-promo-cases.component.html',
  styleUrls: ['./tiny-promo-cases.component.scss'],
})
export class TinyPromoCasesComponent implements OnInit, OnDestroy {
  private readonly apiUrl = environment.apiUrl;
  cases: ManagedCaseSummary[] = [];
  loadError = false;
  loaded = false;
  programMessage = '';
  resultingProgram = '';
  loadedProgram = '';
  promotionId = '';
  program: 'STANDARD' | 'TINYPROMO_MANAGED' | '' = '';
  private routeSub?: Subscription;

  constructor(
    private httpService: HttpService,
    private router: Router,
  ) {}

  statusLabel = managedCaseStatusLabel;
  outcomeLabel = verificationOutcomeLabel;

  confirmed(row: ManagedCaseSummary) {
    return isTerminalVerification(row);
  }

  autoConfirmed(row: ManagedCaseSummary) {
    return row?.verificationOutcome === 'AUTO_CONFIRMED_TIMEOUT';
  }

  ngOnInit() {
    this.routeSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this.isListRoute(event.urlAfterRedirects)) {
        void this.load();
      }
    });
    void this.load();
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  private isListRoute(url: string) {
    const path = (url || '').split('?')[0].replace(/\/$/, '');
    return path.endsWith('promo-managed-cases');
  }

  shortRef(requestId: string) {
    return requestId ? requestId.slice(-8) : '';
  }

  async loadProgram() {
    const promotionId = this.promotionId.trim();
    if (!promotionId) {
      return;
    }
    const url = `${this.apiUrl}/admin-center/hub-promo-commission/v1/promotions/${encodeURIComponent(promotionId)}/program`;
    try {
      const result = await this.httpService.doGet(url).toPromise();
      const saved = readProgramResult(result);
      if (!saved) {
        this.programMessage = 'failed';
      } else {
        this.program = saved;
        this.loadedProgram = saved;
        this.resultingProgram = saved;
        this.programMessage = 'loaded';
      }
    } catch (error) {
      this.programMessage = 'failed';
      console.error('Error loading promo program: ', error);
    }
  }

  async setProgram() {
    const promotionId = this.promotionId.trim();
    if (!promotionId || (this.program !== 'STANDARD' && this.program !== 'TINYPROMO_MANAGED')) {
      return;
    }
    this.programMessage = '';
    const url = `${this.apiUrl}/admin-center/hub-promo-commission/v1/promotions/${promotionId}/program`;
    try {
      const result = await this.httpService.doPut(url, { program: this.program }).toPromise();
      const saved = readProgramResult(result);
      if (!saved) {
        this.restoreProgramSelection();
        this.programMessage = 'failed';
      } else {
        this.resultingProgram = saved;
        this.loadedProgram = saved;
        this.program = saved;
        this.programMessage = 'success';
      }
    } catch (error) {
      this.restoreProgramSelection();
      this.programMessage = 'failed';
      console.error('Error setting promo program: ', error);
    }
  }

  private restoreProgramSelection() {
    if (this.resultingProgram === 'STANDARD' || this.resultingProgram === 'TINYPROMO_MANAGED') {
      this.program = this.resultingProgram;
    }
  }

  private async load() {
    try {
      const url = `${this.apiUrl}/admin-center/tiny-promo-managed-case/v1?size=100&sort=updatedAt,desc`;
      const page = await this.httpService.doGet(url).toPromise();
      this.cases = page?.content || [];
      this.loadError = false;
    } catch (error) {
      this.cases = [];
      this.loadError = true;
      console.error('Error getting managed cases: ', error);
    }
    this.loaded = true;
  }
}
